import { type Logger, TaggedLogger } from '@core/logging/logger'
import {
    type DataStoreGetResult,
    type SynchronousDataStore,
    type DataStoreName,
    StorageType,
    AbstractDataStore,
} from '@core/storage/abstract-data-store.ts'
import { Result } from '@core/models/result'
import { Env } from '../../env.ts'
import { Globals } from '../../globals'
import { ensureError } from '@core/utils/ensure-error'
import { stringFromBase64, stringToBase64 } from '@core/utils/safe-base64'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'
import { DomainConfig } from '@core/types/domain-config.ts'

const getTopDomain = (): string | undefined => {
    const doc = Env._document

    let i,
        h,
        weird_cookie = '_sw_tld_check=cookie',
        hostname = Env._location.hostname.split('.')

    // -2 is used to skip checking against the final TLD
    for (i = hostname.length - 2; i >= 0; i--) {
        h = hostname.slice(i).join('.')
        doc.cookie = weird_cookie + ';domain=.' + h + ';SameSite=strict;'

        if (doc.cookie.indexOf(weird_cookie) > -1) {
            // We were able to store a cookie! This must be it
            doc.cookie =
                weird_cookie.split('=')[0] +
                '=;domain=.' +
                h +
                ';SameSite=strict;expires=Thu, 01 Jan 1970 00:00:01 GMT;'

            return h
        }
    }
}

/**
 * A cookie carrying this store's name plus a domain-key suffix — that is, one this SDK wrote for
 * some domain. The suffix is the trailing 8 characters of a domain key, so it is neither restricted
 * to alphanumerics nor guaranteed to reach 8 characters: nothing enforces a minimum key length.
 * Both bounds are therefore as loose as a cookie name allows, because narrowing either would stop
 * a neighbouring domain being recognised as one, which is the opposite of failing safe.
 *
 * Compiled once. Building it per call would put a fresh RegExp on every storage read taken before
 * a store has migrated to the suffixed name.
 */
const suffixedCookieNameRegex = new RegExp(`^${Globals._pnCookieStorageKey}_[^;=\\s]{1,8}=`)

/** @internal */
export const CookieDataStoreException = {
    notAvailable: () => new PNException(PNExceptionCode.STORAGE_NOT_AVAILABLE, null, ' - Cookie'),
    noDataFor: (key: string) => new PNException(PNExceptionCode.STORAGE_KEY_EMPTY, null, key),
    unableToOpen: (reason: Error) => new PNException(PNExceptionCode.STORAGE_FAILED_TO_OPEN, reason, ` - Cookie`),
} as const

/** @internal */
export class CookieDataStore extends AbstractDataStore implements SynchronousDataStore {
    private static _domainKey?: string | null | undefined
    private static _domain?: DomainConfig | null

    public static _setDomainKey(key: string) {
        this._domainKey = key
    }

    public static _setDomain(domain: DomainConfig) {
        this._domain = domain
    }

    private _preDomainConfigTempData: string | undefined

    public constructor(storeName: DataStoreName | null, logger: Logger = TaggedLogger._getInstance('CookieStore')) {
        super(StorageType.COOKIE, storeName, logger)

        // Seed from dynamically loaded values, without disturbing a domain the SDK has already
        // resolved. Stores are created throughout a page load, and the cookie name is derived
        // from the domain key: resetting it here would rename the cookie mid-session, leaving
        // reads and writes on different cookies.
        CookieDataStore._domainKey ??= Globals._dynamicDomainKey
        CookieDataStore._domain ??= Globals._injectedDomainConfig
    }

    public get domainKey(): string | null | undefined {
        return CookieDataStore._domain?.domain_key ?? CookieDataStore._domainKey
    }

    public get allowedDomains(): string[] | undefined {
        return CookieDataStore._domain?.whitelist_domains
    }

    /**
     * When true the cookie is written to the current hostname only (host-scoped) rather than
     * the registrable root domain. This is the only behaviour controlled by the isolation flag —
     * it prevents sibling subdomains with separate domain keys (e.g. www. vs finance.) from
     * sharing cookie storage. Cookie naming is always domain-key-suffixed regardless of this flag.
     */
    public get isInIsolationMode(): boolean {
        return (
            !!CookieDataStore._domain?.flags.includes(Globals._domainFeatUseIsolatedCookiesFlag) && !!this.domainKey
        )
    }

    /**
     * The last 8 characters of the domain key, used as the cookie name suffix when a domain
     * key is available. This short suffix is intentional — clients with the isolation flag
     * enabled may have allow-listed `_pn_<8chars>` in cookie consent configurations, so the
     * suffix length is a stable contract that must not change.
     */
    public get isolationKey(): string | undefined {
        return this.domainKey?.slice(-8)
    }

    public get _isAvailable(): boolean {
        return !Env._isInWorkerGlobalScope && typeof this._documentCookie === 'string'
    }

    protected get _isUsingLegacyCookies(): boolean {
        return this._storeName === null
    }

    protected get _documentCookie(): string {
        return Env._document.cookie
    }

    protected set _documentCookie(value: string) {
        Env._document.cookie = value
    }

    public async _getData<T>(key: string): Promise<DataStoreGetResult<T>> {
        return this._getDataSync(key)
    }

    public async _setData<T>(key: string, value: T): Promise<Result<T>> {
        return this._setDataSync(key, value)
    }

    public async _removeData(key: string): Promise<Result<null>> {
        return this._removeDataSync(key)
    }

    public _getDataSync<T>(key: string): DataStoreGetResult<T> {
        if (!this._isAvailable) {
            return Result.Err(CookieDataStoreException.notAvailable())
        }

        const openRes = this._openCookie()
        if (openRes.isErr()) {
            return openRes
        }

        const db = openRes.unwrap()

        let value: any
        if (this._isUsingLegacyCookies) {
            value = db[this._buildName(key)]
        } else if (key === Globals._pnStorageTypeLuaKey) {
            value = db[Globals._pnStorageTypeLuaKey] ?? null
        } else {
            value = db[this._storeName!]?.[key]
        }

        if ((value === undefined || value === null) && key !== Globals._pnStorageTypeLuaKey) {
            return Result.Err(CookieDataStoreException.noDataFor(key))
        }

        return Result.Ok({
            value,
            ts: db[Globals._pnStorageTypeLuaKey] ?? 0,
            sourceType: this._storageType,
        })
    }

    public _setDataSync<T>(key: string, value: T): Result<T> {
        const openRes = this._openCookie()
        if (openRes.isErr()) {
            return openRes
        }

        const db = openRes.unwrap()

        let cookieKey: string
        let cookieValue: any

        if (this._isUsingLegacyCookies) {
            cookieKey = key
            cookieValue = value
        } else {
            if (key === Globals._pnStorageTypeLuaKey) {
                db[key] = value
            } else {
                db[this._storeName!] ??= {}
                db[this._storeName!][key] = value
            }

            cookieKey = Globals._pnCookieStorageKey
            cookieValue = db
        }

        this._persist(cookieKey, cookieValue, undefined, key === Globals._pnStorageTypeLuaKey)

        return Result.Ok(value)
    }

    public _removeDataSync(key: string): Result<null> {
        const openRes = this._openCookie()
        if (openRes.isErr()) {
            return openRes
        }

        const db = openRes.unwrap()

        let cookieKey: string | undefined
        let cookieValue: any

        // exDays should ONLY be set if isUsingLegacyCookies.
        // Setting it on the single cookie scope
        // would affect the entire document.
        let exdays: number | undefined

        if (this._isUsingLegacyCookies) {
            cookieKey = key
            cookieValue = null
            exdays = -1
        } else {
            if (db[this._storeName!]) {
                delete db[this._storeName!][key]
                cookieKey = Globals._pnCookieStorageKey
                cookieValue = db
            }
        }

        if (cookieKey) {
            this._persist(cookieKey, cookieValue, exdays)
        }

        return Result.Ok(null)
    }

    protected _openCookie(): Result<Record<string, any>> {
        let db: Record<string, any> = {}

        try {
            const defaultValue = !this._isUsingLegacyCookies ? '{}' : ''

            const allCookieParts = this._documentCookie.split(';').map((record) => record.trim())

            let raw: string | Record<string, any>
            let populatedOwnCookie: string | undefined
            if (!this._isUsingLegacyCookies) {
                // Match exactly our own cookie name: `_pn_<domainKey>=` when a key is available,
                // and the bare `_pn=` before the domain key is known. This prevents a read from
                // picking up a sibling subdomain's cookie or a differently-keyed cookie when
                // multiple _pn_* cookies are visible.
                const ownCookieName = this._buildName(Globals._pnCookieStorageKey)
                const ownCookiePrefix = `${ownCookieName}=`
                const ownCookieParts = allCookieParts.filter((part) => part.startsWith(ownCookiePrefix))

                // A host-scoped and a domain-scoped cookie can share a name, and document.cookie
                // exposes both with no way to tell them apart. An empty value carries no data, so
                // prefer a populated duplicate over whichever copy happens to be listed first.
                populatedOwnCookie = ownCookieParts.find((part) => part.length > ownCookiePrefix.length)
                raw = populatedOwnCookie ?? ownCookieParts[0] ?? ''
            } else {
                // Read the pattern here rather than above the branch: the accessor compiles a new
                // RegExp on each access, and the non-legacy path above never uses it.
                const legacyCookieNameRegex = Globals._pnCookieStorageLegacyKeyPrefixRegex

                raw = Object.fromEntries(
                    allCookieParts.filter((part) => legacyCookieNameRegex.test(part)).map((part) => part.split(/=/)),
                )
            }

            if (!this._isUsingLegacyCookies) {
                let usedPreDomainConfigTempData = false
                if (!raw && this._preDomainConfigTempData) {
                    raw = this._preDomainConfigTempData
                    usedPreDomainConfigTempData = true
                } else {
                    this._preDomainConfigTempData = undefined
                }

                // Nothing of our own to read: fall back to the name used before cookie names
                // carried a domain-key suffix, so state written under the old name stays
                // readable. A pending pre-config write is newer than anything on disk and keeps
                // precedence over it.
                if (!populatedOwnCookie && !usedPreDomainConfigTempData) {
                    raw = this._findUnsuffixedCookie(allCookieParts) ?? raw
                }
            }

            if (raw && typeof raw === 'string') {
                // Split on the first '=' only. The remainder is the value verbatim, which for a
                // base64 payload may itself contain '=' padding.
                const nameValueSeparatorIndex = raw.indexOf('=')
                const rawValue = nameValueSeparatorIndex === -1 ? '' : raw.slice(nameValueSeparatorIndex + 1)

                // Decoding applies to a stored value only. The default is already JSON and must
                // never reach the decoder. A value that does not decode is kept as-is: cookies
                // written before base64 encoding was introduced hold plain JSON.
                let strValue = defaultValue
                if (rawValue) {
                    strValue = !this._isUsingLegacyCookies ? stringFromBase64(rawValue) ?? rawValue : rawValue
                }

                const _db = JSON.parse(strValue)
                if (_db && typeof _db === 'object' && !Array.isArray(_db)) {
                    db = _db
                }
            } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
                db = raw
            } else {
                db = {}
            }
        } catch (err) {
            const error = CookieDataStoreException.unableToOpen(ensureError(err))
            this._logger.warn('Unable to open cookie', error.message)

            return Result.Err(error)
        }

        return Result.Ok(db)
    }

    protected _persist(cookieName: string, dbUpdate: any, exdays?: number, skipLua?: boolean) {
        try {
            const usingLegacyCookies = this._storeName === null

            // set lua value for document updates
            if (
                !usingLegacyCookies &&
                dbUpdate &&
                typeof dbUpdate === 'object' &&
                !Array.isArray(dbUpdate) &&
                skipLua !== true
            ) {
                dbUpdate[Globals._pnStorageTypeLuaKey] = new Date().getTime()
            }

            let cookieValue = dbUpdate
            if (typeof dbUpdate !== 'string') {
                cookieValue = JSON.stringify(dbUpdate)
            }

            if (!usingLegacyCookies) {
                try {
                    cookieValue = stringToBase64(cookieValue)
                } catch {}
            }

            const compositeName = this._buildName(cookieName)
            const kvString = `${compositeName}=${cookieValue}`

            if (!CookieDataStore._domain) {
                this._preDomainConfigTempData = kvString
                return
            }

            const d = new Date()
            d.setTime(d.getTime() + (exdays ?? Globals._pnCookieStorageMaxDays) * 86400 * 1000)
            const expires = cookieValue === null ? '-1' : d.toUTCString()

            const domain = this._getDomain()

            this._documentCookie = `${kvString};expires=${expires};path=/;domain=${domain};SameSite=Strict`
        } catch (err) {
            this._logger.error(ensureError(err))
        }
    }

    /**
     * Finds the un-suffixed `_pn` cookie, the name used before cookie names carried a
     * domain-key suffix.
     *
     * A domain that does not isolate its cookies keeps one root-scoped cookie shared across its
     * subdomains, and on a subdomain whose IndexedDB is a separate origin that cookie is the only
     * carrier of subscriber state. Reading the old name keeps such a subdomain working until a
     * write moves the payload across to the suffixed name.
     *
     * Never read in isolation mode: isolation exists so that domains do not share a cookie, and
     * an un-suffixed name cannot be attributed to any one domain key. For the same reason it is
     * never read once a second domain key stores a suffixed cookie here — ownership of the
     * un-suffixed one is then ambiguous, and adopting it would import a neighbour's identity.
     * An empty cookie carries no data and is ignored, so it cannot mask a populated one of the
     * same name.
     */
    private _findUnsuffixedCookie(cookieParts: string[]): string | undefined {
        const ownCookieName = this._buildName(Globals._pnCookieStorageKey)
        if (this.isInIsolationMode || ownCookieName === Globals._pnCookieStorageKey) {
            return undefined
        }

        const foreignSuffixedCookie = cookieParts.find(
            (part) => suffixedCookieNameRegex.test(part) && !part.startsWith(`${ownCookieName}=`),
        )
        if (foreignSuffixedCookie) {
            return undefined
        }

        const unsuffixedPrefix = `${Globals._pnCookieStorageKey}=`
        return cookieParts.find((part) => part.startsWith(unsuffixedPrefix) && part.length > unsuffixedPrefix.length)
    }

    private _buildName(name: string): string {
        // Only suffix the primary cookie name (_pn). Legacy individual cookies (e.g. _pnss,
        // pushly.user_puuid) are referenced by their exact historical name during migration
        // and must not be altered.
        if (name === Globals._pnCookieStorageKey) {
            return this.isolationKey ? `${name}_${this.isolationKey}` : name
        }
        return name
    }

    private _getDomain(): string {
        let domain: string = Env._location.hostname

        /**
         * use shared storage location - root domain
         * eg: my.site.com -> site.com
         */
        if (!this.isInIsolationMode) {
            if (this.allowedDomains?.length == 1) {
                domain = this.allowedDomains[0]
            } else {
                const topDomain = getTopDomain()
                if (topDomain) {
                    domain = topDomain
                }
            }

            if (!domain) {
                domain = Env._location.hostname
                if (domain !== 'localhost') {
                    domain = `.${domain.split('.').splice(-2).join('.')}`
                }
            }
        }

        return domain
    }
}
